import { useState } from "react";
import { apiCall } from "./api";
import LegalPanel, { LegalLink } from "./legal-panel";
import blackOwlLogo from "./assets/black-owl-logo.png";
import { FONT_BODY, FONT_DISPLAY } from "./theme";

// --- Owl logo image ---
function OwlLogo({ size = 150 }) {
  return (
    <img
      src={blackOwlLogo}
      alt="Black Owl"
      style={{ display: "block", margin: "0 auto", width: size, height: "auto", objectFit: "contain" }}
    />
  );
}

// --- Animated football field lines background ---
function FieldBackground() {
  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 0, overflow: "hidden",
      background: "linear-gradient(165deg, #0a0a14 0%, #0f1a0f 40%, #0a0a14 100%)",
    }}>
      {/* Yard lines */}
      {Array.from({ length: 12 }).map((_, i) => (
        <div key={i} style={{
          position: "absolute",
          left: 0, right: 0,
          top: `${(i + 1) * 8}%`,
          height: "1px",
          background: `rgba(201, 162, 39, ${i === 5 ? 0.12 : 0.04})`,
        }}/>
      ))}
      {/* Hash marks */}
      {Array.from({ length: 12 }).map((_, i) => (
        <div key={`h${i}`} style={{
          position: "absolute",
          top: `${(i + 1) * 8}%`,
          left: "30%",
          width: "8px",
          height: "1px",
          background: "rgba(201, 162, 39, 0.08)",
        }}/>
      ))}
      {/* Subtle vignette */}
      <div style={{
        position: "absolute", inset: 0,
        background: "radial-gradient(ellipse at center, transparent 40%, rgba(0,0,0,0.6) 100%)",
      }}/>
    </div>
  );
}

// --- Input component ---
function Input({ label, type = "text", value, onChange, placeholder, icon }) {
  const [focused, setFocused] = useState(false);
  return (
    <div style={{ marginBottom: "20px" }}>
      <label style={{
        display: "block",
        fontSize: "11px",
        fontWeight: 600,
        letterSpacing: "2px",
        textTransform: "uppercase",
        color: focused ? "#c9a227" : "#7a7a8e",
        marginBottom: "8px",
        transition: "color 0.3s ease",
        fontFamily: FONT_BODY,
      }}>
        {label}
      </label>
      <div style={{
        position: "relative",
        display: "flex",
        alignItems: "center",
      }}>
        <span style={{
          position: "absolute",
          left: "14px",
          fontSize: "16px",
          color: focused ? "#c9a227" : "#4a4a5e",
          transition: "color 0.3s ease",
          pointerEvents: "none",
        }}>
          {icon}
        </span>
        <input
          type={type}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          style={{
            width: "100%",
            padding: "14px 14px 14px 42px",
            fontSize: "15px",
            fontFamily: FONT_BODY,
            color: "#e8e6df",
            background: focused ? "rgba(201, 162, 39, 0.06)" : "rgba(255,255,255,0.03)",
            border: `1.5px solid ${focused ? "#c9a227" : "rgba(255,255,255,0.08)"}`,
            borderRadius: "8px",
            outline: "none",
            transition: "all 0.3s ease",
            boxSizing: "border-box",
          }}
        />
      </div>
    </div>
  );
}

// --- Main App ---
export default function BlackOwlLogin({ onLoginSuccess }) {
  const [mode, setMode] = useState("login"); // "login" or "signup"
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [showLegal, setShowLegal] = useState(null); // null | "terms" | "privacy"

  const handleSubmit = async () => {
    setError("");
    setSuccess("");

    if (!username.trim() || !password.trim()) {
      setError("Please fill in all fields.");
      return;
    }

    if (mode === "signup") {
      if (password.length < 6) {
        setError("Password must be at least 6 characters.");
        return;
      }
      if (password !== confirmPassword) {
        setError("Passwords do not match.");
        return;
      }
      if (!agreedToTerms) {
        setError("You must agree to the Terms of Service and Privacy Policy.");
        return;
      }
    }

    setLoading(true);
    try {
      const endpoint = mode === "login" ? "login" : "signUp";
      const result = await apiCall(endpoint, { username: username.trim(), password });

      if (result.status === "success") {
        if (mode === "login") {
          setSuccess(`Welcome back, ${username}! ${result.lobby ? `Rejoining lobby ${result.lobby}...` : "Redirecting to home..."}`);
          if (onLoginSuccess) onLoginSuccess(username.trim(), result.lobby, result.paid_user);
        } else {
          setSuccess("Account created! You can now log in.");
          setTimeout(() => {
            setMode("login");
            setSuccess("");
            setConfirmPassword("");
          }, 2000);
        }
      }
    } catch (err) {
      setError(err.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const toggleMode = () => {
    setMode(mode === "login" ? "signup" : "login");
    setError("");
    setSuccess("");
    setConfirmPassword("");
  };

  return (
    <div style={{
      minHeight: "100vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontFamily: FONT_BODY,
      position: "relative",
    }}>
      <FieldBackground />

      <div style={{
        position: "relative",
        zIndex: 1,
        width: "100%",
        maxWidth: "400px",
        padding: "20px",
      }}>
        {/* Card */}
        <div style={{
          background: "rgba(12, 12, 22, 0.85)",
          backdropFilter: "blur(20px)",
          border: "1px solid rgba(201, 162, 39, 0.15)",
          borderRadius: "16px",
          padding: "48px 36px 36px",
          boxShadow: "0 24px 80px rgba(0,0,0,0.5), inset 0 1px 0 rgba(201,162,39,0.08)",
        }}>
          {/* Logo & Title */}
          <div style={{ textAlign: "center", marginBottom: "36px" }}>
            <div style={{
              display: "inline-block",
              animation: "fadeIn 0.8s ease",
            }}>
              <OwlLogo size={210} />
            </div>
            <h1 style={{
              fontSize: "28px",
              fontWeight: 700,
              color: "#c9a227",
              margin: "16px 0 4px",
              letterSpacing: "4px",
              textTransform: "uppercase",
              fontFamily: FONT_DISPLAY,
            }}>
              Black Owl
            </h1>
            <p style={{
              fontSize: "12px",
              color: "#5a5a6e",
              letterSpacing: "3px",
              textTransform: "uppercase",
              margin: 0,
              fontFamily: FONT_BODY,
            }}>
              Fantasy Draft Game
            </p>
          </div>

          {/* Tab switcher */}
          <div style={{
            display: "flex",
            marginBottom: "28px",
            background: "rgba(255,255,255,0.03)",
            borderRadius: "8px",
            padding: "3px",
            border: "1px solid rgba(255,255,255,0.05)",
          }}>
            {["login", "signup"].map((m) => (
              <button
                key={m}
                onClick={() => { setMode(m); setError(""); setSuccess(""); }}
                style={{
                  flex: 1,
                  padding: "10px",
                  fontSize: "12px",
                  fontWeight: 600,
                  letterSpacing: "2px",
                  textTransform: "uppercase",
                  fontFamily: FONT_BODY,
                  color: mode === m ? "#0a0a14" : "#5a5a6e",
                  background: mode === m ? "#c9a227" : "transparent",
                  border: "none",
                  borderRadius: "6px",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                }}
              >
                {m === "login" ? "Log In" : "Sign Up"}
              </button>
            ))}
          </div>

          {/* Form fields */}
          <Input
            label="Username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="Enter your username"
            icon="⚡"
          />
          <Input
            label="Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Enter your password"
            icon="🔒"
          />
          {mode === "signup" && (
            <Input
              label="Confirm Password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm your password"
              icon="🔒"
            />
          )}

          {/* Terms consent checkbox (signup only) */}
          {mode === "signup" && (
            <div style={{
              display: "flex", alignItems: "flex-start", gap: "10px",
              marginBottom: "16px", padding: "4px 2px",
            }}>
              <button
                onClick={() => setAgreedToTerms(!agreedToTerms)}
                style={{
                  width: "22px", height: "22px", flexShrink: 0,
                  marginTop: "1px",
                  borderRadius: "6px",
                  border: `2px solid ${agreedToTerms ? "#c9a227" : "rgba(255,255,255,0.2)"}`,
                  background: agreedToTerms ? "#c9a227" : "transparent",
                  cursor: "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  transition: "all 0.2s ease",
                }}
              >
                {agreedToTerms && (
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#0a0a14" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                )}
              </button>
              <p style={{
                fontSize: "12px", color: "#8a8a9e", margin: 0,
                lineHeight: 1.5, fontFamily: FONT_BODY,
              }}>
                I am 18 or older and agree to the{" "}
                <LegalLink label="Terms of Service" onClick={() => setShowLegal("terms")} />
                {" "}and{" "}
                <LegalLink label="Privacy Policy" onClick={() => setShowLegal("privacy")} />.
              </p>
            </div>
          )}

          {/* Error / Success messages */}
          {error && (
            <div style={{
              padding: "12px 16px",
              marginBottom: "16px",
              background: "rgba(220, 50, 50, 0.1)",
              border: "1px solid rgba(220, 50, 50, 0.25)",
              borderRadius: "8px",
              color: "#e85555",
              fontSize: "13px",
              fontFamily: FONT_BODY,
            }}>
              {error}
            </div>
          )}
          {success && (
            <div style={{
              padding: "12px 16px",
              marginBottom: "16px",
              background: "rgba(50, 180, 80, 0.1)",
              border: "1px solid rgba(50, 180, 80, 0.25)",
              borderRadius: "8px",
              color: "#4ade6a",
              fontSize: "13px",
              fontFamily: FONT_BODY,
            }}>
              {success}
            </div>
          )}

          {/* Submit button */}
          <button
            onClick={handleSubmit}
            disabled={loading}
            style={{
              width: "100%",
              padding: "16px",
              fontSize: "14px",
              fontWeight: 700,
              letterSpacing: "3px",
              textTransform: "uppercase",
              fontFamily: FONT_BODY,
              color: "#0a0a14",
              background: loading
                ? "rgba(201, 162, 39, 0.5)"
                : "linear-gradient(135deg, #c9a227 0%, #e0be4a 50%, #c9a227 100%)",
              border: "none",
              borderRadius: "8px",
              cursor: loading ? "wait" : "pointer",
              transition: "all 0.3s ease",
              boxShadow: loading ? "none" : "0 4px 20px rgba(201, 162, 39, 0.25)",
            }}
            onMouseEnter={(e) => {
              if (!loading) e.target.style.boxShadow = "0 6px 30px rgba(201, 162, 39, 0.4)";
            }}
            onMouseLeave={(e) => {
              if (!loading) e.target.style.boxShadow = "0 4px 20px rgba(201, 162, 39, 0.25)";
            }}
          >
            {loading ? "..." : mode === "login" ? "Enter the Draft" : "Create Account"}
          </button>

          {/* Toggle link */}
          <p style={{
            textAlign: "center",
            marginTop: "24px",
            fontSize: "13px",
            color: "#5a5a6e",
          }}>
            {mode === "login" ? "New to Black Owl? " : "Already have an account? "}
            <span
              onClick={toggleMode}
              style={{
                color: "#c9a227",
                cursor: "pointer",
                fontWeight: 600,
                textDecoration: "underline",
                textUnderlineOffset: "3px",
              }}
            >
              {mode === "login" ? "Create an account" : "Log in"}
            </span>
          </p>
        </div>

        {/* Footer */}
        <p style={{
          textAlign: "center",
          marginTop: "24px",
          fontSize: "11px",
          color: "#3a3a4e",
          letterSpacing: "1px",
          fontFamily: FONT_BODY,
        }}>
          Draft smart. Play bold. Win big.
        </p>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        * { box-sizing: border-box; }
        input::placeholder { color: #3a3a4e; }
      `}</style>

      {showLegal && (
        <LegalPanel
          initialTab={showLegal}
          onClose={() => setShowLegal(null)}
        />
      )}
    </div>
  );
}
